import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, ThemeMode, ThemeContextType } from '../types/theme';
import { createTheme } from '../config/theme';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('dark'); // Default to dark theme
  const [theme, setTheme] = useState<Theme>(() => {
    const initialTheme = createTheme('dark');

    // Set initial CSS variables
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      root.style.setProperty('--color-background', initialTheme.colors.background);
      root.style.setProperty('--color-text-primary', initialTheme.colors.text.primary);
      root.style.setProperty('--color-text-secondary', initialTheme.colors.text.secondary);
      root.style.setProperty('--color-text-disabled', initialTheme.colors.text.disabled);
      root.style.setProperty('--color-primary', initialTheme.colors.primary);
      root.style.setProperty('--color-secondary', initialTheme.colors.secondary);
      root.style.setProperty('--color-surface', initialTheme.colors.surface);
      root.style.setProperty('--color-border', initialTheme.colors.border);
      root.style.setProperty('--color-error', initialTheme.colors.error);
      root.style.setProperty('--color-warning', initialTheme.colors.warning);
      root.style.setProperty('--color-success', initialTheme.colors.success);
      root.style.setProperty('--color-info', initialTheme.colors.info);

      // Brand colors
      root.style.setProperty('--color-brand-25', initialTheme.colors.brand[25]);
      root.style.setProperty('--color-brand-50', initialTheme.colors.brand[50]);
      root.style.setProperty('--color-brand-100', initialTheme.colors.brand[100]);
      root.style.setProperty('--color-brand-200', initialTheme.colors.brand[200]);
      root.style.setProperty('--color-brand-300', initialTheme.colors.brand[300]);
      root.style.setProperty('--color-brand-400', initialTheme.colors.brand[400]);
      root.style.setProperty('--color-brand-500', initialTheme.colors.brand[500]);
      root.style.setProperty('--color-brand-600', initialTheme.colors.brand[600]);
      root.style.setProperty('--color-brand-700', initialTheme.colors.brand[700]);
      root.style.setProperty('--color-brand-800', initialTheme.colors.brand[800]);
      root.style.setProperty('--color-brand-900', initialTheme.colors.brand[900]);
      root.style.setProperty('--color-brand-950', initialTheme.colors.brand[950]);

      // Brand RGB values for rgba()
      root.style.setProperty('--color-brand-500-rgb', '158, 119, 237');
      root.style.setProperty('--color-brand-400-rgb', '182, 146, 246');
    }

    return initialTheme;
  });

  useEffect(() => {
    const newTheme = createTheme(mode);
    setTheme(newTheme);

    // Set CSS variables for SCSS modules
    const root = document.documentElement;
    root.style.setProperty('--color-background', newTheme.colors.background);
    root.style.setProperty('--color-text-primary', newTheme.colors.text.primary);
    root.style.setProperty('--color-text-secondary', newTheme.colors.text.secondary);
    root.style.setProperty('--color-text-disabled', newTheme.colors.text.disabled);
    root.style.setProperty('--color-primary', newTheme.colors.primary);
    root.style.setProperty('--color-secondary', newTheme.colors.secondary);
    root.style.setProperty('--color-surface', newTheme.colors.surface);
    root.style.setProperty('--color-border', newTheme.colors.border);
    root.style.setProperty('--color-error', newTheme.colors.error);
    root.style.setProperty('--color-warning', newTheme.colors.warning);
    root.style.setProperty('--color-success', newTheme.colors.success);
    root.style.setProperty('--color-info', newTheme.colors.info);

    // Brand colors
    root.style.setProperty('--color-brand-25', newTheme.colors.brand[25]);
    root.style.setProperty('--color-brand-50', newTheme.colors.brand[50]);
    root.style.setProperty('--color-brand-100', newTheme.colors.brand[100]);
    root.style.setProperty('--color-brand-200', newTheme.colors.brand[200]);
    root.style.setProperty('--color-brand-300', newTheme.colors.brand[300]);
    root.style.setProperty('--color-brand-400', newTheme.colors.brand[400]);
    root.style.setProperty('--color-brand-500', newTheme.colors.brand[500]);
    root.style.setProperty('--color-brand-600', newTheme.colors.brand[600]);
    root.style.setProperty('--color-brand-700', newTheme.colors.brand[700]);
    root.style.setProperty('--color-brand-800', newTheme.colors.brand[800]);
    root.style.setProperty('--color-brand-900', newTheme.colors.brand[900]);
    root.style.setProperty('--color-brand-950', newTheme.colors.brand[950]);

    // Brand RGB values for rgba()
    root.style.setProperty('--color-brand-500-rgb', '158, 119, 237');
    root.style.setProperty('--color-brand-400-rgb', '182, 146, 246');
  }, [mode]);

  const handleSetMode = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  const value: ThemeContextType = {
    theme,
    mode,
    setMode: handleSetMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
